import {
	type BoundActions,
	createActionsHook,
	type HookActionsFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type {
	AlphaExtendedThinkingMode,
	AutoActionApprovalMode,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { type RovoControlsSnapshot } from './ChatModeInternal';
import { store } from './store';
import type {
	RovoApp,
	OrgKnowledge,
	ReasoningMode,
	SourceMode,
	ChatMode,
	ChatModeState,
	WebSearchDefaultOptions,
} from './types';

export const useChatModeStoreActions: HookActionsFunction<
	BoundActions<
		ChatModeState,
		{
			setAutoActionApprovalMode: (
				key: string,
				autoActionApprovalMode: AutoActionApprovalMode,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setReasoningMode: (
				key: string,
				reasoningMode: ReasoningMode,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			resetReasoningMode: (key: string) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setDeepResearchEnabled: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setWebSearchEnabled: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			seedWebSearchDefault: (
				key: string,
				webSearchDefaultEnabled: boolean,
			) => ({ dispatch, getState }: StoreActionApi<ChatModeState>) => boolean;
			setSourceMode: (
				key: string,
				sourceMode: SourceMode,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setOrgKnowledge: (
				key: string,
				orgKnowledge: OrgKnowledge,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setAppFiltersFromPrompt: (
				key: string,
				filteredApps: RovoApp[],
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			addAppFilter: (
				key: string,
				app: RovoApp,
			) => ({ getState, dispatch }: StoreActionApi<ChatModeState>) => void;
			setUseCurrentPageContext: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setSpaceSourceCount: (
				key: string,
				count?: number,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setSelectedCloudIds: (
				key: string,
				selectedCloudIds: string[],
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setChatMode: (
				key: string,
				chatMode: Partial<
					Omit<ChatMode, 'orgKnowledge' | 'deepResearchEnabled' | 'thinkDeeperEnabled'>
				>,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			getChatMode: (key: string) => ({ getState }: StoreActionApi<ChatModeState>) => {
				appFilters: RovoApp[] | undefined;
				selectedCloudIds: string[];
				appFiltersSetByPrompt: boolean;
				deepResearchEnabled: boolean;
				thinkDeeperEnabled: boolean;
				fastModeEnabled: boolean;
				autoReasoningEnabled: boolean;
				alphaExtendedThinkingMode: AlphaExtendedThinkingMode | undefined;
				autoActionApprovalMode: AutoActionApprovalMode;
				taskModeEnabled: boolean;
				reasoningMode: ReasoningMode;
				webSearchEnabled: boolean;
				sourceMode: SourceMode;
				orgKnowledge: OrgKnowledge;
				autoApplyEnabled: boolean;
				useCurrentPageContext: boolean;
				spaceSourceCount?: number;
			};
			clearMode: (
				key: string,
				options?: WebSearchDefaultOptions,
			) => ({ dispatch, getState }: StoreActionApi<ChatModeState>) => void;
			setRovoControlsSuppressed: (
				key: string,
				options: { suppressed: boolean },
			) => ({ dispatch, getState }: StoreActionApi<ChatModeState>) => RovoControlsSnapshot | null;
		}
	>
> = createActionsHook(store);
